import { useEffect, useState } from 'react'
import type { GameListing, RoomView } from '@v2/multiplayer'
import { Icon } from '@v2/react'
import { api, type ConnectionStatus, type RoomConnection } from './network'
import { Page, Wordmark } from './page'
import { ConnectionBadge } from './PlayScreen'
import { MemberList } from './RoomPanel'

/** The lobby is a short list: who is here, which game, one button. */
export function Lobby({ room, connection, status, pending, error, onDismissError }: { room: RoomView; connection: RoomConnection; status: ConnectionStatus; pending: boolean; error: string | null; onDismissError: () => void }) {
  const [games, setGames] = useState<GameListing[]>([])
  useEffect(() => { let cancelled = false; void api.games().then(list => { if (!cancelled) setGames(list) }).catch(() => {}); return () => { cancelled = true } }, [])
  const leader = room.you?.leader ?? false
  const disabled = pending || status !== 'connected'
  const count = room.members.length
  const game = room.game
  const fit = game ? count >= game.players.min && count <= game.players.max : false
  const needs = game ? count < game.players.min ? `Needs ${game.players.min - count} more ${game.players.min - count === 1 ? 'player' : 'players'}` : count > game.players.max ? `Seats at most ${game.players.max}` : `${count} ${count === 1 ? 'player' : 'players'}, ready` : 'Pick a game'
  const control = (action: Parameters<RoomConnection['control']>[0]) => { void connection.control(action).catch(() => {}) }
  return <Page>
    <header className="pg-masthead pg-masthead-row"><Wordmark/><ConnectionBadge status={status} pending={pending} spectating={false}/></header>
    {error && <p className="pg-error" role="alert">{error} <button type="button" className="pg-link" onClick={onDismissError}>Dismiss</button></p>}
    <section className="pg-card" aria-label="Table">
      <div className="pg-code-row">
        <div><span className="pg-label">Table code</span><strong className="pg-code-big" data-testid="room-code">{room.code}</strong></div>
        <CopyLink url={room.inviteUrl}/>
      </div>
      <div className="pg-game-row">
        <span className="pg-label">Game</span>
        {leader && games.length > 1 ? <select className="pg-select" aria-label="Game" value={game?.id ?? ''} disabled={disabled} onChange={event => { void connection.chooseGame(event.target.value).catch(() => {}) }}>
          {!game && <option value="">Choose a game</option>}
          {games.map(option => <option key={option.id} value={option.id}>{option.name}</option>)}
        </select> : <strong>{game?.name ?? 'Not chosen yet'}</strong>}
        {game && <small className="pg-muted">{game.players.min === game.players.max ? `${game.players.max} players` : `${game.players.min}–${game.players.max} players`}</small>}
      </div>
    </section>
    <section className="pg-card" aria-label="Players">
      <div className="pg-section-row"><h2>Players</h2><small className="pg-muted" data-testid="player-count">{needs}</small></div>
      <MemberList room={room} connection={connection} disabled={disabled}/>
      {leader && game && count < game.players.max && <button type="button" className="pg-add" disabled={disabled} onClick={() => control('/bots')}><Icon name="bot" size={15}/>Add a bot</button>}
    </section>
    <div className="pg-actions">
      {leader
        ? <button className="tt-primary-button pg-wide" disabled={disabled || !fit} onClick={() => control('/start')}>Start game<Icon name="arrow" size={14}/></button>
        : <p className="pg-muted" role="status">Waiting for {room.members.find(member => member.leader)?.name ?? 'the leader'} to start.</p>}
      <button type="button" className="pg-link" disabled={pending} onClick={() => { void connection.control('/leave').then(() => location.assign('/')).catch(() => {}) }}>Leave the table</button>
    </div>
  </Page>
}

export function CopyLink({ url, compact = false }: { url: string; compact?: boolean }) {
  const [copied, setCopied] = useState(false)
  const [reveal, setReveal] = useState(false)
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 2000) }
    catch { setReveal(true) }
  }
  return <div className={`pg-copy${compact ? ' is-compact' : ''}`}>
    <button type="button" className="tt-secondary-button" onClick={() => { void copy() }}><Icon name={copied ? 'check' : 'copy'} size={14}/>{copied ? 'Copied' : 'Copy invite link'}</button>
    {reveal && <input className="tt-input" aria-label="Invite link" readOnly value={url} onFocus={event => event.target.select()}/>}
    <span className="tt-sr-only" role="status">{copied ? 'Invite link copied.' : ''}</span>
  </div>
}
